import { DeferredPromise } from '@cf-shared/shared-util';
import { awaitBoundedLive } from './couch-login-wait-bound';
import { isDatabaseShuttingDown } from './database-unavailable-error';

/** What the push listener does with an error thrown while it handled a tap. */
export enum PushTapFailureHandling {
  /** The person was being signed out, so the tap cannot be honoured: record it quietly and do
   *  not navigate, because the app is already reloading to the login page. */
  DropForSignOut = 'DropForSignOut',
  /** A real failure: report it as a problem and land the person on the daily log. */
  ReportAndOpenHome = 'ReportAndOpenHome',
}

/**
 * Decides how the push listener treats an error from a tap (df-72eb80b3f2c5).
 *
 * A tap that runs while a sign-out is shutting the user database down (a warm resume whose
 * background login found the session ended) has its read refused with a shutdown reason. That
 * is our own sign-out doing its job, the same split `sentry-error-policy.ts` makes.
 * `isDatabaseShuttingDown` is the test, NOT `isDatabaseTornDown`: the wider one also counts a
 * missing database handle, which happens at early boot too and is a real failure here.
 */
export function decidePushTapFailureHandling(err: unknown): PushTapFailureHandling {
  return isDatabaseShuttingDown(err) ? PushTapFailureHandling.DropForSignOut : PushTapFailureHandling.ReportAndOpenHome;
}

/**
 * How long a push tap waits for the signed-in app to be ready before it is dropped (#6180).
 *
 * A tap that launches the app goes through warm resume, which is ready in well under a
 * second plus the navigation restore. A cold start that has to sync can take much longer,
 * which is why this is generous. It is reached when nobody is signed in (Ready never fires
 * on the login page) or when the boot is stuck, which the boot watchdogs already report.
 */
export const PUSH_TAP_READY_WAIT_LIMIT_MS = 120_000;

/**
 * Says whether the signed-in app has finished loading, so a push tap knows when it may read
 * the user database and navigate (#6180).
 *
 * The boot calls `markReady` at the same moments it calls `notifySignedInAppReady` in
 * `app.component.ts`: cold Ready, warm resume once the navigation restore has finished, and
 * the restore-wedged rescue. Waiting for the database alone would not be enough — a tap that
 * navigates before the navigation restore is replaced by it. See
 * `design-docs/push-notifications.md`, "Push tap handler".
 */
export class PushTapReadyGate {
  private readonly released = DeferredPromise.defer<void>();
  private isReleased = false;
  private readyMarked = false;
  private pendingHolds = 0;

  markReady() {
    this.readyMarked = true;
    this.releaseIfClear();
  }

  /**
   * Keeps waiting taps held until `work` settles, even once the app is marked ready.
   *
   * For the navigation-restore rescue: it re-roots to home, and the boot marks the app ready
   * while that re-root is still running. A tap released then would open its page only to have
   * the re-root replace it. Has no effect once the gate has released.
   */
  holdUntil(work: Promise<unknown>) {
    if (this.isReleased) return;
    this.pendingHolds++;
    const releaseHold = () => {
      this.pendingHolds--;
      this.releaseIfClear();
    };
    work.then(releaseHold, releaseHold);
  }

  /**
   * Waits until released, with no time limit. Never rejects.
   *
   * For a remembered notification link (#6573), which may rightly wait through a sign-in on
   * the login page. A tap uses `waitAsync` instead, so a stale tap is dropped.
   */
  async waitUntilReadyAsync(): Promise<void> {
    await this.released;
  }

  /** Waits until released, but no longer than `limitMs`. Never rejects. */
  async waitAsync(limitMs: number): Promise<{ ready: boolean, waitedMs: number }> {
    if (this.isReleased) return { ready: true, waitedMs: 0 };
    const result = await awaitBoundedLive(this.released, limitMs);
    return { ready: !result.timedOut, waitedMs: result.waitedMs };
  }

  private releaseIfClear() {
    // if the app is ready and no rescue re-root is still running, let waiting taps through
    if (this.isReleased || !this.readyMarked || this.pendingHolds > 0) return;
    this.isReleased = true;
    this.released.resolve();
  }
}

/**
 * Runs a push tap's work once the app is ready (#6180). If the app is not ready within
 * `limitMs`, the work is dropped and `onGaveUp` is told how long it waited; a later
 * `markReady` does not run it, so a stale tap never navigates over whatever is on screen by
 * then. An error thrown by `run` propagates, so the caller still reports real failures.
 */
export async function runPushTapWhenReadyAsync(
  gate: PushTapReadyGate,
  limitMs: number,
  run: () => Promise<void>,
  onGaveUp: (waitedMs: number) => void,
): Promise<void> {
  const answer = await gate.waitAsync(limitMs);
  if (!answer.ready) {
    onGaveUp(answer.waitedMs);
    return;
  }
  await run();
}
